import type {
  DynamicSectionType,
  OnLinkItemClick,
  OnQuickNavOpen,
  OnSubItemLinkClick,
  SidebarItem,
  SidebarLink,
  SidebarSubMenu,
} from './types';

import type { ConstantSidebarItemData } from './sidebar-constants';
import {
  constantSidebarItemData,
  constantSidebarKeys,
  ESidebarItemTypes,
  manageAppsItemData,
  sidebarReferral,
} from './sidebar-constants';
import type { BiLogger, Router } from '@wix/app-market-core';
import type { IRoute } from '@wix/app-market-core-types';
import { Route, parseStringToBi } from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import type { DynamicSection, SubcategoryLink } from '@wix/app-market-services';
import { SectionType } from '@wix/app-market-services';
import type { TFunction } from 'react-i18next';
import {
  appMarketMenuNavigation,
  appMarketHoverOverSidebarItemSrc24Evid186,
} from '@wix/bi-logger-app-market-data/v2';

/**
 * SidebarService manages the creation and navigation of sidebar items.
 * It maps dynamic-sections into sidebar items, handles item clicks, and generates route-based href links.
 *
 * @class SidebarService
 *
 * @method get selectedItemKey - Returns the key of the currently selected sidebar item based on the route.
 * @method get sidebarItems - Retrieves both dynamic and constant sidebar items.
 * @method get showHeader - Determines if the header should be shown based on `metaSiteId`.
 * @method get showFooter - Determines if the footer should be shown based on `metaSiteId`.
 * @method onItemClick - Handles navigation when a sidebar item is clicked.
 * @method onSubItemClick - Handles navigation when a sub-item in the sidebar is clicked.
 * @method onQuickNavOpen - Handles opening quick navigation items, logging interactions.
 * @method onFooterClick - Handles the click event on the "Manage Apps" footer item, logging the interaction and navigating to the `MANAGE_APPS` route.
 */

export class SidebarService {
  private readonly router: Router;
  private readonly route: IRoute;
  private readonly baseURL: string | undefined;
  private readonly t: TFunction;
  private readonly biLogger: BiLogger;
  private readonly horizontalNav: boolean;

  public readonly sidebarItems: SidebarItem[];
  public readonly selectedItemKey: string;
  public readonly showHeader: boolean;
  public readonly showFooter: boolean;

  constructor({
    sections,
    route,
    router,
    baseURL,
    t,
    biLogger,
    metaSiteId,
    horizontalNav = false,
  }: {
    sections: DynamicSectionType[];
    route: IRoute;
    router: Router;
    baseURL: string | undefined;
    t: TFunction;
    biLogger: BiLogger;
    metaSiteId: string | undefined;
    horizontalNav?: boolean;
  }) {
    this.router = router;
    this.route = route;
    this.baseURL = baseURL;
    this.t = t;
    this.biLogger = biLogger;
    this.horizontalNav = horizontalNav;
    this.sidebarItems = this.dynamicSectionsToSidebarItems(sections);
    this.selectedItemKey = this.routeToSidebarItemKey(route);
    this.showHeader = !!metaSiteId;
    this.showFooter = !!metaSiteId;
  }

  onFooterClick(): void {
    const manageAppsItem = this.sidebarItemDataToSidebarItem({
      sidebarItemData: manageAppsItemData,
    }) as SidebarLink;
    this.onItemClick(manageAppsItem, undefined);
  }

  onItemClick: OnLinkItemClick = (item, index): void => {
    if (this.selectedItemKey === item.key) {
      return;
    }
    this.biLogger.report(
      appMarketMenuNavigation({
        menu_item_label: parseStringToBi(item.key),
        item_type: parseStringToBi(item.route.path),
        index,
        page_type: parseStringToBi(this.route.path),
        page_name: parseStringToBi(this.selectedItemKey),
      }),
    );
    this.router.navigateTo(item.route);
  };

  onQuickNavOpen: OnQuickNavOpen = (item, index): void => {
    this.biLogger.report(
      appMarketHoverOverSidebarItemSrc24Evid186({
        menu_item_label: parseStringToBi(item.key),
        item_type: parseStringToBi(item.route.path),
        index,
        page_type: parseStringToBi(this.route.path),
        page_name: parseStringToBi(this.selectedItemKey),
      }),
    );
  };

  onSubItemClick: OnSubItemLinkClick = (item, index, parentItem): void => {
    if (this.selectedItemKey === item.key) {
      return;
    }
    this.biLogger.report(
      appMarketMenuNavigation({
        menu_item_label: parseStringToBi(item.key),
        item_type: parseStringToBi(item.route.path),
        index,
        page_type: parseStringToBi(this.route.path),
        page_name: parseStringToBi(this.selectedItemKey),
        item_section: parseStringToBi(
          this.routeToSidebarItemKey(parentItem.route),
        ),
      }),
    );
    this.router.navigateTo(item.route);
  };

  private dynamicSectionsToSidebarItems(
    sections: DynamicSectionType[],
  ): SidebarItem[] {
    const sidebarItems = sections.map((section) =>
      this.mapSidebarSectionToSidebarItem(section, this.baseURL),
    );

    const allItems = this.getConstantSidebarItems().concat(sidebarItems);
    return this.horizontalNav ? this.toFlatSidebarItems(allItems) : allItems;
  }

  /**
   * Flattens sidebar items for the horizontal nav: keeps only the top-level
   * items layer - sub-menus become plain links (their sub-items are dropped),
   * and titles/dividers are omitted.
   */
  private toFlatSidebarItems(items: SidebarItem[]): SidebarItem[] {
    return items
      .filter(
        (item): item is SidebarLink | SidebarSubMenu =>
          item.type === ESidebarItemTypes.LINK ||
          item.type === ESidebarItemTypes.SUB_MENU,
      )
      .map((item) =>
        item.type === ESidebarItemTypes.SUB_MENU
          ? {
              type: ESidebarItemTypes.LINK,
              label: item.label,
              key: item.key,
              route: item.route,
              href: item.href,
            }
          : item,
      );
  }

  private mapSidebarSectionToSidebarItem(
    section: DynamicSection,
    baseURL: string | undefined,
  ): SidebarItem {
    switch (section.type) {
      case SectionType.SIDEBAR_DIVIDER:
        return {
          type: ESidebarItemTypes.DIVIDER,
        };
      case SectionType.SIDEBAR_TITLE:
        return {
          type: ESidebarItemTypes.TITLE,
          label: section.sidebarTitle?.text ?? '',
        };
      case SectionType.COLLECTION_LINK:
        const collectionRoute: IRoute = {
          path: RoutePath.COLLECTION,
          payload: {
            slug: section.collectionLink?.slug ?? '',
            referral: sidebarReferral,
          },
        };
        return {
          type: ESidebarItemTypes.LINK,
          label: section.collectionLink?.label ?? '',
          key: this.routeToSidebarItemKey(collectionRoute),
          route: collectionRoute,
          href: this.routeToHref(collectionRoute, baseURL),
          highlighted: !!section.collectionLink?.highlighted,
        };
      case SectionType.CATEGORY_LINK:
        const categoryRoute: IRoute = {
          path: RoutePath.CATEGORY,
          payload: {
            slug: section.categoryLink?.slug ?? '',
            referral: sidebarReferral,
          },
        };
        return {
          type: ESidebarItemTypes.SUB_MENU,
          label: section.categoryLink?.label ?? '',
          key: this.routeToSidebarItemKey(categoryRoute),
          route: categoryRoute,
          href: this.routeToHref(categoryRoute, baseURL),
          links:
            section.categoryLink?.subCategoryLinks?.map(
              (subCategoryLink: SubcategoryLink) => {
                const subCategoryRoute: IRoute = {
                  path: RoutePath.SUB_CATEGORY,
                  payload: {
                    parentSlug: subCategoryLink.parentCategorySlug ?? '',
                    slug: subCategoryLink.slug ?? '',
                    subCat: subCategoryLink.slug ?? '',
                    referral: sidebarReferral,
                  },
                };
                return {
                  type: ESidebarItemTypes.LINK,
                  label: subCategoryLink.label ?? '',
                  key: this.routeToSidebarItemKey(subCategoryRoute),
                  route: subCategoryRoute,
                  href: this.routeToHref(subCategoryRoute, baseURL),
                };
              },
            ) ?? ([] as SidebarLink[]),
        };
      default:
        throw new Error(
          `[sidebar-actions] (mapSidebarSectionToSidebarItem) failed: section type: ${section.type} is not supported on sidebar`,
        );
    }
  }

  private getConstantSidebarItems(): SidebarItem[] {
    return constantSidebarItemData.map((item) =>
      this.sidebarItemDataToSidebarItem({ sidebarItemData: item }),
    );
  }

  private sidebarItemDataToSidebarItem({
    sidebarItemData,
  }: {
    sidebarItemData: ConstantSidebarItemData;
  }): SidebarItem {
    const { key, labelTransKey, route } = sidebarItemData;
    return {
      type: ESidebarItemTypes.LINK,
      label: this.t(labelTransKey),
      key,
      route,
      href: this.routeToHref(route, this.baseURL),
    } as SidebarLink;
  }

  private routeToHref(route: IRoute, baseURL: string | undefined): string {
    const { path, payload } = route;
    const currentRoute = new Route({ path, payload, baseURL });
    return currentRoute.toHref();
  }

  private routeToSidebarItemKey(route: IRoute): string {
    const { payload, path } = route;
    const defaultKey = constantSidebarKeys[RoutePath.UNSUPPORTED];

    switch (path) {
      case RoutePath.HOME:
      case RoutePath.SEARCH_RESULTS:
      case RoutePath.MANAGE_APPS:
        return constantSidebarKeys[path];
      case RoutePath.SUB_CATEGORY:
        /* the horizontal nav has no sub-items, so a sub-category page keeps
           its parent category item selected */
        if (this.horizontalNav) {
          return payload.parentSlug
            ? `${constantSidebarKeys[RoutePath.CATEGORY]}-${payload.parentSlug}`
            : defaultKey;
        }
        return payload.slug
          ? `${constantSidebarKeys[path]}-${payload.slug}`
          : defaultKey;
      case RoutePath.COLLECTION:
      case RoutePath.CATEGORY:
      case RoutePath.APP_PAGE:
      case RoutePath.DEVELOPER:
        return payload.slug
          ? `${constantSidebarKeys[path]}-${payload.slug}`
          : defaultKey;
      default:
        return defaultKey;
    }
  }
}
